import React, { useState, useEffect } from 'react';

import styled from 'styled-components';
import { colorimetry } from 'utils/utils.color';

import { SliderArrows } from './components/SliderArrows';
import { getNextStep, getPrevStep } from './utils';
import { SliderSlideTitle } from './components/SliderSlideTitle';
import { WidgetSliderSlide } from './types';
import { SliderDots } from './components/SliderDots';
import { SliderImage } from './components/SliderImage';

const CSSWidgetSlider = styled.div<{ maxHeight: number }>`
    overflow: hidden;
    position: relative;
    max-height: ${(props) => props.maxHeight}px;
`;

const CSSWidgetSliderWrapper = styled.div<{ steps: number; step: number }>`
    position: relative;
    width: ${(props) => props.steps * 100}%;
    left: ${(props) => -props.step * 100}%;
    display: flex;
    transition: left 0.4s;
`;

const CSSWidgetSliderSlide = styled.div<{ maxHeight: number }>`
    position: relative;
    display: flex;
    width: 100%;
    max-height: ${(props) => props.maxHeight}px;
`;

const CSSWidgetSliderOverlay = styled.div<{ color: string; backgroundColor: string }>`
    color: ${(props) => props.color};
    background-color: ${(props) => props.backgroundColor};
    border-radius: 8px;
    position: absolute;
    left: 12px;
    bottom: 30px;
    right: 12px;
    padding: 12px;
`;

const CSSWidgetSliderDotsWrapper = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 25px;
`;

export type JRCWidgetSliderProps = {
    slides: WidgetSliderSlide[];
    maxWidth: number;
    loop: boolean;
    useDots?: boolean;
    startAt?: number;
    onClickSlide?: (index: number) => void;
};

export const JRCWidgetSlider = ({
    slides,
    maxWidth = 800,
    loop = false,
    useDots = true,
    startAt = 0,
    onClickSlide,
}: JRCWidgetSliderProps) => {
    const steps = Array.isArray(slides) ? slides.length : 0;
    const [step, setStep] = useState<number>(startAt > steps - 1 ? 0 : startAt);
    const [loaded, setLoaded] = useState<number[]>([0, 1, 2]);
    const [maxHeight, setMaxHeight] = useState<number>(maxWidth / (16 / 9));

    useEffect(() => {
        setMaxHeight(maxWidth / (16 / 9));
    }, [maxWidth]);

    const onClickNext = () => {
        setStep(getNextStep(step, steps, loop));
        const lastLoadedIndex = loaded.at(-1);
        if (lastLoadedIndex && lastLoadedIndex < steps) {
            setLoaded([...loaded, lastLoadedIndex + 1]);
        }
    };
    const onClickPrev = () => setStep(getPrevStep(step, steps, loop));

    return Array.isArray(slides) && slides.length ? (
        <CSSWidgetSlider maxHeight={maxHeight}>
            <CSSWidgetSliderWrapper step={step} steps={steps}>
                {slides.map((slide, index) => {
                    const { opacityColor, opacityFilter, titleHeading, textColor } = slide;
                    return (
                        <CSSWidgetSliderSlide maxHeight={maxHeight} key={slide.uri}>
                            {loaded.includes(index) ? (
                                <SliderImage
                                    onClick={() => (onClickSlide ? onClickSlide(index) : undefined)}
                                    initialUri={slide.uri}
                                    displayAs={slide.displayAs}
                                    backgroundPosition={slide.backgroundPosition}
                                    backgroundSize={slide.backgroundSize}
                                    maxWidth={maxWidth}
                                    maxHeight={maxHeight}
                                />
                            ) : (
                                <></>
                            )}
                            {(slide.title !== '' || slide.text !== '') && (
                                <CSSWidgetSliderOverlay
                                    color={textColor ?? '#fff'}
                                    backgroundColor={
                                        opacityColor && opacityFilter
                                            ? colorimetry.hex(opacityColor).toRgbaColor(opacityFilter)
                                            : 'rgba(0,0,0,.4)'
                                    }>
                                    <SliderSlideTitle
                                        title={slide.title}
                                        titleHeading={titleHeading}></SliderSlideTitle>
                                    <div>{slide.text}</div>
                                </CSSWidgetSliderOverlay>
                            )}
                        </CSSWidgetSliderSlide>
                    );
                })}
            </CSSWidgetSliderWrapper>
            {useDots && slides.length > 1 && (
                <CSSWidgetSliderDotsWrapper>
                    <SliderDots step={step} steps={steps} onClick={setStep} />
                </CSSWidgetSliderDotsWrapper>
            )}
            {steps && (
                <SliderArrows
                    step={step}
                    total={steps}
                    loop={loop}
                    onClickNext={onClickNext}
                    onClickPrev={onClickPrev}
                />
            )}
        </CSSWidgetSlider>
    ) : (
        <></>
    );
};
